import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaFileAlt, FaGraduationCap } from 'react-icons/fa';
import { mbaAPI } from '../utils/api';
import '../styles/UniversityDetail.scss';

const TYPE_DETAILS = {
  'Desk Project': {
    title: 'Desk Project (Secondary Data Study)',
    description: 'A study based entirely on existing secondary data sources. Ideal for students who want to do research on financial performance, stock market models, historical company data, or theoretical literature reviews.',
    features: [
      'No primary questionnaire or survey is required.',
      'Data is sourced from published financial reports, databases, journals, or online sources.',
      'Heavy focus on literature review, ratio analysis, financial modeling, or statistical regression.',
      'Highly suited for Specializations like Finance, International Business, and Corporate Strategy.'
    ],
    tips: 'Ensure your secondary sources are authentic and correctly cited. Focus on producing strong analytical charts and interpretations.'
  },
  'Field Project': {
    title: 'Field Project (Primary Data Study)',
    description: 'A research work involving direct primary data collection from consumers, employees, or targeted groups using surveys, interviews, or on-field observation.',
    features: [
      'Requires preparing a formal structured questionnaire.',
      'Involves sampling (e.g., sample size of 50-100 respondents).',
      'Data is analyzed using statistical tools like SPSS, Excel, ANOVA, chi-square, or charts.',
      'Highly suited for Specializations like Marketing, HR, and Operations.'
    ],
    tips: 'Attach your blank questionnaire in the Annexure/Appendix. Ensure your sample representation matches your study objectives.'
  },
  'OJT Project': {
    title: 'OJT Project (On-Job Training / Internship)',
    description: 'A report documenting practical internship experience, operational processes, and problem-solving assignments undertaken inside an active corporate organization.',
    features: [
      'Based on a minimum 30-day or 60-day training duration.',
      'Focuses on company workflow, department structure, and hands-on tasks performed.',
      'Requires highlighting a specific process gap or business issue and proposing solutions.',
      'Highly suited for HR onboarding, Sales campaigns, and Supply Chain training.'
    ],
    tips: 'A corporate internship certificate with supervisor approval signatures is absolutely mandatory to attach.'
  },
  'Research Project': {
    title: 'Research Project (Scientific Hypothesis Testing)',
    description: 'A rigorous academic and scientific study focused on testing structured hypotheses using advanced statistical methods.',
    features: [
      'Involves formulating null (H0) and alternate (H1) hypotheses.',
      'Applies advanced statistical testing (t-test, Z-test, f-test, regression models).',
      'Requires detailed variable definitions and research methodology.',
      'Highly suited for academic advancement and research-oriented roles.'
    ],
    tips: 'Strictly adhere to the standard APA citation style. Pay extra attention to validation and reliability tests of the data.'
  }
};

const getTypeIcon = (typeName) => {
  if (!typeName) return '📁';
  const lower = typeName.toLowerCase();
  if (lower.includes('desk')) return '💻';
  if (lower.includes('field')) return '📊';
  if (lower.includes('ojt') || lower.includes('job') || lower.includes('internship')) return '💼';
  if (lower.includes('research')) return '🔬';
  return '📁';
};

const getDetailsForType = (typeName) => {
  if (!typeName) return null;
  const lower = typeName.toLowerCase();
  if (lower.includes('desk')) return TYPE_DETAILS['Desk Project'];
  if (lower.includes('field')) return TYPE_DETAILS['Field Project'];
  if (lower.includes('ojt') || lower.includes('job') || lower.includes('internship')) return TYPE_DETAILS['OJT Project'];
  if (lower.includes('research')) return TYPE_DETAILS['Research Project'];
  return null;
};

const TAB_LABELS = {
  guidelines: 'Guidelines',
  reportFormat: 'Report Format',
  indexFormat: 'Index Format',
  certificateFormat: 'Certificate Format',
  instructions: 'Instructions',
  formatting: 'Formatting Rules',
  structure: 'Report Structure',
  submission: 'Submission Info',
  viva: 'Viva Guidelines',
  evaluation: 'Evaluation Criteria'
};

const UniversityDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [university, setUniversity] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Project Type Explorer state
  const [selectedTypeId, setSelectedTypeId] = useState('');

  // Guidelines Active Tab
  const [activeGuidelineTab, setActiveGuidelineTab] = useState('guidelines');

  // Fetch initial university data & catalog (types only)
  useEffect(() => {
    const fetchCatalog = async () => {
      setLoading(true);
      setError('');
      try {
        const [uniRes, typeRes] = await Promise.all([
          mbaAPI.getUniversityBySlug(slug),
          mbaAPI.getTypes(),
        ]);
        
        setUniversity(uniRes.data?.data || null);
        
        const typeData = Array.isArray(typeRes.data?.data) ? typeRes.data.data : [];
        setTypes(typeData);

        // Pre-select the first type
        if (typeData.length > 0) {
          setSelectedTypeId(typeData[0].id);
        }
      } catch (err) {
        setError('Failed to fetch university details.');
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
  }, [slug]);

  // Compute active tabs list dynamically based on what guidelines are actually filled in
  const activeTabsList = useMemo(() => {
    if (!university) return [];
    const keys = [
      'guidelines',
      'reportFormat',
      'indexFormat',
      'certificateFormat',
      'instructions',
      'formatting',
      'structure',
      'submission',
      'viva',
      'evaluation'
    ];
    return keys.filter(key => university[key] && university[key].trim());
  }, [university]);

  // Keep active tab synced to available tabs
  useEffect(() => {
    if (activeTabsList.length > 0) {
      if (!activeTabsList.includes(activeGuidelineTab)) {
        setActiveGuidelineTab(activeTabsList[0]);
      }
    }
  }, [activeTabsList, activeGuidelineTab]);

  const activeTypeObj = useMemo(() => {
    return types.find((t) => t.id === Number(selectedTypeId)) || null;
  }, [types, selectedTypeId]);

  const typeDetails = useMemo(() => {
    return activeTypeObj ? getDetailsForType(activeTypeObj.name) : null;
  }, [activeTypeObj]);

  const handleDownloadTextGuidelines = () => {
    if (!university) return;
    let text = `MBA PROJECT GUIDELINES: ${university.name}\n`;
    text += `==================================================\n\n`;

    if (university.guidelines) text += `GUIDELINES:\n${university.guidelines}\n\n`;
    if (university.reportFormat) text += `REPORT FORMAT:\n${university.reportFormat}\n\n`;
    if (university.indexFormat) text += `INDEX FORMAT:\n${university.indexFormat}\n\n`;
    if (university.certificateFormat) text += `CERTIFICATE FORMAT:\n${university.certificateFormat}\n\n`;
    if (university.instructions) text += `INSTRUCTIONS:\n${university.instructions}\n\n`;
    if (university.formatting) text += `FORMATTING RULES:\n${university.formatting}\n\n`;
    if (university.structure) text += `REPORT STRUCTURE:\n${university.structure}\n\n`;
    if (university.submission) text += `SUBMISSION INSTRUCTIONS:\n${university.submission}\n\n`;
    if (university.viva) text += `VIVA VOCE GUIDELINES:\n${university.viva}\n\n`;
    if (university.evaluation) text += `EVALUATION CRITERIA:\n${university.evaluation}\n\n`;

    text += `Generated on ${new Date().toLocaleDateString()}\n`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${university.name.replace(/\s+/g, '_')}_Guidelines.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTabContent = () => {
    if (!university) return null;
    const text = university[activeGuidelineTab];

    if (!text) return <p className="no-data-msg">No guidelines added for this section.</p>;

    return (
      <ul className="guidelines-list">
        {text.split('\n').filter(l => l.trim()).map((line, idx) => (
          <li key={idx}>{line.trim()}</li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="university-detail-page loading-state">
        <div className="loader">Loading University Guidelines...</div>
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className="university-detail-page error-state">
        <h2>University Not Found</h2>
        <p>{error || 'The university you are looking for does not exist.'}</p>
        <button onClick={() => navigate('/mba-guidelines')} className="btn-back">
          <FaArrowLeft /> Back to University Guidelines
        </button>
      </div>
    );
  }

  const backendBase = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8000';

  // Render logo helper for large header
  const renderHeaderLogo = () => {
    const logo = university.logo;
    if (!logo) return <span className="uni-logo-large">🎓</span>;
    if (logo.startsWith('/') || logo.startsWith('http')) {
      return <img src={`${backendBase}${logo}`} alt={university.name} className="uni-logo-large-img" style={{ width: '80px', height: '80px', borderRadius: '16px', objectFit: 'contain', background: 'white', padding: '6px' }} />;
    }
    return <span className="uni-logo-large">{logo}</span>;
  };

  return (
    <div className="university-detail-page">
      <div className="back-link-container">
        <button className="btn-back-link" onClick={() => navigate('/mba-guidelines')}>
          <FaArrowLeft /> Back to University Guidelines
        </button>
      </div>

      {/* University Main Banner */}
      <section className="university-header-card">
        <div className="logo-wrapper">
          {renderHeaderLogo()}
        </div>
        <div className="uni-details">
          <span className="mba-badge"><FaGraduationCap /> MBA PORTAL</span>
          <h1>{university.name}</h1>
          <p className="description">Explore specific formatting specifications, submission deadlines, assessment methods, and customized project databases below.</p>
          
          <div className="download-center">
            {university.document_url && (
              <a
                href={`${backendBase}${university.document_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action-download"
                download
              >
                <FaDownload /> Download Guidelines PDF
              </a>
            )}
            {university.sample_report_url && (
              <a
                href={`${backendBase}${university.sample_report_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action-download btn-secondary"
                download
              >
                <FaFileAlt /> Download Sample Report
              </a>
            )}
            <button onClick={handleDownloadTextGuidelines} className="btn-action-download btn-text-only">
              📄 Export Text Guidelines
            </button>
          </div>
        </div>
      </section>

      {/* Main Grid Layout: Guidelines and Project Types */}
      <div className="university-main-content">
        
        {/* Left Section: University Guidelines Tabs */}
        <section className="guidelines-card">
          <div className="card-header">
            <h2>University Guidelines</h2>
            <p>Official curriculum specifications and guidelines for MBA project work.</p>
          </div>

          {activeTabsList.length === 0 ? (
            <div className="tab-body">
              <p className="no-data-msg">No guidelines configured for this university yet.</p>
            </div>
          ) : (
            <>
              <div className="guideline-tabs">
                {activeTabsList.map((tab) => (
                  <button
                    key={tab}
                    className={`tab-link ${activeGuidelineTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveGuidelineTab(tab)}
                  >
                    {TAB_LABELS[tab] || tab}
                  </button>
                ))}
              </div>

              <div className="tab-body">
                {renderTabContent()}
              </div>
            </>
          )}
        </section>

        {/* Right Section: Interactive Project Type Explorer */}
        <section className="types-explorer-card">
          <div className="card-header">
            <h2>Project Types</h2>
            <p>Click a type card below to update the project directory filter and view specific requirements.</p>
          </div>

          <div className="types-grid">
            {types.map((t) => {
              const active = selectedTypeId === t.id;
              return (
                <div
                  key={t.id}
                  className={`type-card ${active ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTypeId(t.id);
                  }}
                >
                  <div className="type-icon">{getTypeIcon(t.name)}</div>
                  <h3>{t.name}</h3>
                </div>
              );
            })}
          </div>

          {typeDetails && (
            <div className="type-details-panel">
              <div className="panel-header">
                <h4>{typeDetails.title}</h4>
                <p>{typeDetails.description}</p>
              </div>
              
              <div className="panel-content">
                <h5>Key Specifications:</h5>
                <ul>
                  {typeDetails.features.map((feat, idx) => (
                    <li key={idx}>{feat}</li>
                  ))}
                </ul>

                <div className="tip-box">
                  <strong>💡 Pro-Tip:</strong> {typeDetails.tips}
                </div>
              </div>
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default UniversityDetail;
